import React, { useState, useRef, useEffect, useMemo } from 'react';
import GapNote from './GapNote';
import SilentNote from './SilentNote';
import { playRange, stopAll, gapSkips } from '../utils/audio';
import { splitToken } from '../utils/wordText';
import { MEDIA_BASE, MEDIA_V } from '../api';

function getAudioUrl(sentence) {
  // A sentence marked silent has a clip, but it decodes to a flat zero — treat it
  // as having none so the row's continuous play steps straight over it instead of
  // sitting through it (the longest of them runs 24 s).
  if (sentence.silent) return null;
  return sentence.audio ? `${MEDIA_BASE}/audio/${sentence.audio}${MEDIA_V}` : null;
}

const CJK = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九'];

// 文 (whole story) — ported from the seediq-app chapter mechanism (d84c5fe):
// one card per PARAGRAPH (sentences flow as prose inside), and the story's
// 7 parts (text.sections, {n,title,from,to} 段-id ranges) page ONE PART AT A
// TIME with a sticky 目錄 pull-down + ‹ › and 上一部/下一部 nav. ▶ plays
// continuously to the end of the CURRENT part.
export default function ParagraphView({ text, activeSentId, onVisibleSentence, onPlayingWord, onWordClick, getWordStatus, onChapterChange, focusSentId = null, focusWIdx = -1, displayWord = (w) => w }) {
  const [playingId, setPlayingId]     = useState(null);
  const [activeWordIdx, setActiveWordIdx] = useState(-1);
  const [showZh, setShowZh]           = useState({});
  const activeSentRef = useRef(null);
  const seqRef        = useRef(null);

  const paragraphs = useMemo(() => (text.paragraphs || []).filter(p => (p.sentences || []).length), [text.paragraphs]);
  const allSents   = useMemo(() => paragraphs.flatMap(p => p.sentences), [paragraphs]);

  // Parts: {n, title, from, to} with 段 ids — every story has 7; page by part.
  const chapters = useMemo(() => {
    const secs = text.sections || [];
    if (!secs.length) return null;
    const idx = (pid) => paragraphs.findIndex(p => p.id === pid);
    const ch = secs.map(sec => ({ sec, from: idx(sec.from), to: idx(sec.to) }))
                   .filter(c => c.from >= 0 && c.to >= c.from);
    return ch.length ? ch : null;
  }, [text.sections, paragraphs]);
  const [chapIdx, setChapIdx] = useState(0);

  // Land (and stay) on the part containing the active sentence, so a view
  // switch or 跳到故事 opens the right page.
  useEffect(() => {
    if (!chapters || !activeSentId) return;
    const pi = paragraphs.findIndex(p => p.sentences.some(s => s.id === activeSentId));
    if (pi < 0) return;
    const ci = chapters.findIndex(c => pi >= c.from && pi <= c.to);
    if (ci >= 0 && ci !== chapIdx) setChapIdx(ci);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSentId, chapters, paragraphs]);

  const shownParas = chapters
    ? paragraphs.slice(chapters[chapIdx].from, chapters[chapIdx].to + 1)
    : paragraphs;
  const chapterSents = useMemo(() => shownParas.flatMap(p => p.sentences), [shownParas]);

  useEffect(() => {
    if (activeSentRef.current)
      activeSentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeSentId]);

  // Report the sentence at the top of the viewport (view-switch anchoring).
  useEffect(() => {
    if (!onVisibleSentence) return;
    let raf = 0;
    const scan = () => {
      raf = 0;
      const els = document.querySelectorAll('.story-row[data-sid]');
      let best = null, bestTop = Infinity;
      els.forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.bottom > 110 && r.top < bestTop) { bestTop = r.top; best = el; }
      });
      if (best) onVisibleSentence(best.getAttribute('data-sid'));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(scan); };
    window.addEventListener('scroll', onScroll, { passive: true });
    scan();
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [onVisibleSentence, chapIdx]);

  // While playing, the playing sentence IS the current one for view switches.
  useEffect(() => {
    if (playingId && onVisibleSentence) onVisibleSentence(playingId);
  }, [playingId, onVisibleSentence]);

  useEffect(() => {
    if (!onPlayingWord) return;
    if (!playingId || activeWordIdx < 0) { onPlayingWord(null); return; }
    onPlayingWord({ sentId: playingId, wIdx: activeWordIdx });
  }, [playingId, activeWordIdx, onPlayingWord]);
  useEffect(() => () => { if (onPlayingWord) onPlayingWord(null); }, [onPlayingWord]);

  function playSentenceItem(sentence, onDone) {
    const url = getAudioUrl(sentence);
    if (!url) { onDone?.(); return; }
    const segStart = sentence.audioStart ?? null;
    const segEnd   = sentence.audioEnd   ?? null;
    const words    = sentence.words || [];
    setPlayingId(sentence.id);
    setActiveWordIdx(-1);
    playRange(url, segStart, segEnd,
      (relTime) => {
        const idx = words.findIndex((w, i) =>
          relTime >= w.start && (i === words.length - 1 || relTime < words[i + 1].start));
        setActiveWordIdx(idx);
      },
      () => { setPlayingId(null); setActiveWordIdx(-1); onDone?.(); },
      undefined, undefined,
      gapSkips(words.map(w => ({ start: (segStart ?? 0) + w.start, end: (segStart ?? 0) + w.end })))
    );
  }

  function playFromSentence(sentences, startIdx) {
    seqRef.current = { sentences, idx: startIdx };
    function next() {
      const seq = seqRef.current;
      if (!seq || seq.idx >= seq.sentences.length) {
        setPlayingId(null); setActiveWordIdx(-1); seqRef.current = null; return;
      }
      playSentenceItem(seq.sentences[seq.idx], () => {
        if (seqRef.current) { seqRef.current.idx++; next(); }
      });
    }
    next();
  }

  // ▶ on a card: continuous play from HERE to the end of the current part
  // (to the story's end when there are no parts); ❚❚ on the playing card stops.
  function handleRowPlay(sentList, e) {
    e.stopPropagation();
    if (sentList.some(s => s.id === playingId)) {
      stopAll(); seqRef.current = null; setPlayingId(null); return;
    }
    stopAll(); seqRef.current = null;
    const pool = chapters ? chapterSents : allSents;
    const i = pool.findIndex(s => s.id === sentList[0].id);
    playFromSentence(pool, Math.max(0, i));
  }

  function handleWordClick(word, wordIdx, sentence, e) {
    e.stopPropagation();
    const url      = getAudioUrl(sentence);
    const segStart = sentence.audioStart ?? null;
    const segEnd   = sentence.audioEnd   ?? null;
    // Shift-tap = explicit "play from here to the sentence end"; no word-view.
    if (e.shiftKey) {
      if (url) {
        const absStart = (segStart ?? 0) + word.start;
        const words   = sentence.words || [];
        const sentEnd = segEnd ?? ((segStart ?? 0) + words[words.length - 1]?.end + 0.1);
        setPlayingId(sentence.id);
        setActiveWordIdx(wordIdx);
        playRange(url, absStart, sentEnd,
          (relTime) => {
            const local = relTime + word.start;
            const idx = words.findIndex((w, i) =>
              local >= w.start && (i === words.length - 1 || local < words[i + 1].start));
            setActiveWordIdx(idx);
          },
          () => { setPlayingId(null); setActiveWordIdx(-1); },
          undefined, undefined,
          gapSkips(words.map(w => ({ start: (segStart ?? 0) + w.start, end: (segStart ?? 0) + w.end })))
        );
      }
      return;
    }
    // Plain tap = focus the word (open word-view); the Reader focus-effect autoplays it — incl 文.
    onWordClick(word, wordIdx, sentence.id, url, segStart, e);
  }

  function renderWords(sentence) {
    const sentPlaying = playingId === sentence.id;
    return (sentence.words || []).map((word, wi) => {
      const { lead, core, trail } = splitToken(word.text, text.dialect);
      return (
        <React.Fragment key={`${sentence.id}-${wi}`}>
          {lead && <span className="word-punct">{lead}</span>}
          {core && (
            <span
              className={`word status-${getWordStatus(core)}${sentPlaying && wi === activeWordIdx ? ' word-active' : ''}${sentence.id === focusSentId && wi === focusWIdx ? ' wordview-focus' : ''}`}
              onClick={(e) => handleWordClick({ ...word, text: core }, wi, sentence, e)}
            >{displayWord(core)}</span>
          )}
          {trail && <span className="word-punct">{trail}</span>}
          {' '}
        </React.Fragment>
      );
    });
  }

  function renderParagraph(p) {
    const sents     = p.sentences;
    const isActive  = sents.some(s => s.id === activeSentId);
    const isPlaying = sents.some(s => s.id === playingId);
    const zhOn      = !!showZh[p.id];
    const zh        = sents.map(s => s.translation || '').join('');
    const gaps      = sents.flatMap(s => s.gaps || []);
    const mute      = sents.find(s => s.silent);
    const silentNote = mute
      ? (sents.every(s => s.silent) ? mute.silentNote : '錄音部分從缺：本段有句子的音檔為空白，待補錄')
      : null;
    const anchorId  = sents.find(s => s.id === playingId)?.id
                   ?? sents.find(s => s.id === activeSentId)?.id
                   ?? sents[0].id;
    return (
      <div key={p.id} data-sid={anchorId} className="story-row" ref={isActive ? activeSentRef : null}>
        <button className="story-play" onClick={(e) => handleRowPlay(sents, e)}>
          {isPlaying ? '❚❚' : '▶'}
        </button>
        <div className="story-body">
          <div className="story-line">{sents.map(renderWords)}</div>
          <GapNote gaps={gaps} />
          <SilentNote note={silentNote} />
          {zh && (
            <div className="sv-cnwrap">
              <button className="sv-cnlink" onClick={(e) => { e.stopPropagation(); setShowZh(s => ({ ...s, [p.id]: !s[p.id] })); }}>
                {zhOn ? '隱藏中文' : '顯示中文'}
              </button>
              {zhOn && <p className="sv-trans">{zh}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  const goChap = (i) => {
    stopAll(); seqRef.current = null; setPlayingId(null); setChapIdx(i); window.scrollTo(0, 0);
    // keep the title-bar 目錄 (Reader.itemIdx) in step with 文's part paging
    const first = chapters && paragraphs[chapters[i].from]?.sentences?.[0];
    if (first && onChapterChange) onChapterChange(first.id);
  };

  return (
    <div className="story-view">
      {/* Act navigation now lives in the title-bar 目錄 (Reader.js); this view keeps
          the section head as a reading chapter title + the bottom part-paging nav. */}
      {chapters && (
        <div className="story-section-head">
          <div className="ssh-title"><span className="ssh-n">{CJK[chapters[chapIdx].sec.n]}</span>{chapters[chapIdx].sec.title}</div>
        </div>
      )}
      {shownParas.map(renderParagraph)}
      {chapters && (
        <div className="story-chapnav">
          <button className="sv-navbtn" disabled={chapIdx === 0} onClick={() => goChap(chapIdx - 1)}>‹ 上</button>
          <span className="sv-navpos">{chapIdx + 1} / {chapters.length}</span>
          <button className="sv-navbtn" disabled={chapIdx === chapters.length - 1} onClick={() => goChap(chapIdx + 1)}>下 ›</button>
        </div>
      )}
      <div style={{ height: 60 }} />
    </div>
  );
}
